import { useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, Banknote, X } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import {
  useAccounts, useCreerFactureFournisseur, useFacturesFournisseurs, usePayerFactureFournisseur, useReceptionsAFacturer,
} from '@/hooks/useJournal';
import { useContacts } from '@/hooks/useCrm';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import { formatNumber } from '@/utils/currency';
import type { FactureFournisseur } from '@/api/finance';

const F = (c: string | bigint) => formatNumber(Number(BigInt(c)) / 100);
const date = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const enCentimes = (v: string) => Math.round(parseFloat(v.replace(/\s/g, '').replace(',', '.') || '0') * 100);
const STATUTS: Record<string, string> = { draft: 'À payer', partial: 'Partiellement payée', paid: 'Payée', cancelled: 'Annulée' };
const CHAMP = { display: 'grid', gap: 4, fontSize: 12, color: 'var(--tx-2)' } as const;
const MODES = [{ v: 'virement', l: 'Virement' }, { v: 'cheque', l: 'Chèque' }, { v: 'especes', l: 'Espèces' }, { v: 'mobile_money', l: 'Mobile money' }];

/**
 * Factures fournisseurs — lot 2, étape 2.5.
 * Rattachée à une réception (la dette est déjà comptée, la facture la confirme au franc près) ou directe (loyer,
 * électricité, prestations : écriture d'achat automatique). Paiement total ou partiel par le DAF ; facture soldée → lettrée.
 */
export const FacturesFournisseursPage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const peutSaisir = role === 'daf' || role === 'acheteur';
  const peutPayer = role === 'daf';
  const [statut, setStatut] = useState('');
  const [nouvelle, setNouvelle] = useState(false);
  const [aPayer, setAPayer] = useState<FactureFournisseur | null>(null);
  const { data: factures = [], isLoading, refetch } = useFacturesFournisseurs(statut || undefined);

  const totalDu = factures.filter((f) => f.status !== 'paid').reduce((s, f) => s + BigInt(f.totalCents) - BigInt(f.paidAmountCents), 0n);
  const echues = factures.filter((f) => f.status !== 'paid' && new Date(f.dueDate) < new Date()).length;

  return (
    <>
      <Topbar
        title="Factures fournisseurs"
        subtitle="À payer, partiellement payées, payées — rattachées à une réception ou directes"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir"><RefreshCw size={14} /></button>
            {peutSaisir && <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setNouvelle(true)}><Plus size={14} /> Nouvelle facture</button>}
          </>
        }
      />
      <div className="ds-content">
        <div className="ds-stats-bar">
          <div className="ds-stat-card"><div><div className="ds-stat-val">{F(totalDu)}</div><div className="ds-stat-lbl">Reste à payer (FCFA)</div></div></div>
          <div className="ds-stat-card"><div><div className="ds-stat-val">{factures.filter((f) => f.status !== 'paid').length}</div><div className="ds-stat-lbl">Factures non soldées</div></div></div>
          <div className="ds-stat-card"><div><div className="ds-stat-val" style={echues ? { color: 'var(--danger)' } : undefined}>{echues}</div><div className="ds-stat-lbl">Échues</div></div></div>
        </div>

        <div style={{ display: 'flex', gap: 6, margin: '12px 0', flexWrap: 'wrap' }}>
          {[['', 'Toutes'], ['draft', 'À payer'], ['partial', 'Partielles'], ['paid', 'Payées']].map(([v, l]) => (
            <button key={v} type="button" className={`ds-btn ds-btn-sm ${statut === v ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setStatut(v)}>{l}</button>
          ))}
        </div>

        {nouvelle && <NouvelleFacture onClose={() => setNouvelle(false)} />}
        {aPayer && <Paiement facture={aPayer} onClose={() => setAPayer(null)} />}

        <div className="ds-table-panel">
          <table className="ds-tbl" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>N° interne</th><th>Fournisseur</th><th>N° fournisseur</th><th>Date</th><th>Échéance</th><th>Origine</th>
                <th style={{ textAlign: 'right' }}>TTC</th><th style={{ textAlign: 'right' }}>Payé</th><th>Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} style={{ padding: 16, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && factures.length === 0 && <tr><td colSpan={10} style={{ padding: 16, color: 'var(--tx-3)' }}>Aucune facture fournisseur.</td></tr>}
              {factures.map((f) => {
                const echue = f.status !== 'paid' && new Date(f.dueDate) < new Date();
                return (
                  <tr key={f.id}>
                    <td><span className="ds-ref-code">{f.reference}</span></td>
                    <td style={{ fontSize: 12 }}>{f.contact.companyName ?? f.contact.fullName}</td>
                    <td style={{ fontSize: 12 }}>{f.numeroFournisseur}</td>
                    <td style={{ fontSize: 12 }}>{date(f.issueDate)}</td>
                    <td style={{ fontSize: 12, color: echue ? 'var(--danger)' : undefined }}>{date(f.dueDate)}</td>
                    <td style={{ fontSize: 12 }}>{f.reception ? `Réception ${f.reception.reference}` : 'Directe'}</td>
                    <td className="ds-am">{F(f.totalCents)}</td>
                    <td className="ds-am">{F(f.paidAmountCents)}</td>
                    <td style={{ fontSize: 12 }}>{STATUTS[f.status] ?? f.status}</td>
                    <td>{peutPayer && f.status !== 'paid' && (
                      <button className="ds-row-btn" title="Payer" onClick={() => setAPayer(f)}><Banknote size={13} /></button>
                    )}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/** Saisie d'une facture : depuis une réception (montant prérempli) ou directe (fournisseur, compte, HT, TVA). */
const NouvelleFacture = ({ onClose }: { onClose: () => void }) => {
  const [mode, setMode] = useState<'reception' | 'directe'>('reception');
  const [receptionId, setReceptionId] = useState('');
  const [contactId, setContactId] = useState('');
  const [compte, setCompte] = useState('');
  const [numero, setNumero] = useState('');
  const [dateF, setDateF] = useState(aujourdhui());
  const [echeance, setEcheance] = useState('');
  const [ht, setHt] = useState('');
  const [tva, setTva] = useState('');
  const [ttc, setTtc] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const { data: receptions = [] } = useReceptionsAFacturer();
  const { data: fournisseursPage } = useContacts({ contactType: 'supplier', limit: 100 });
  const { data: comptes = [] } = useAccounts();
  const creer = useCreerFactureFournisseur();
  const comptesCharge = useMemo(() => comptes.filter((c) => c.code.startsWith('6') || c.code.startsWith('2')), [comptes]);
  const choisirReception = (id: string) => {
    setReceptionId(id);
    const r = receptions.find((x) => x.id === id);
    if (r) setTtc(String(Number(BigInt(r.ttcCents)) / 100));
  };
  const ttcDirect = enCentimes(ht) + enCentimes(tva);

  const envoyer = async (e: FormEvent) => {
    e.preventDefault(); setErreur(null);
    try {
      await creer.mutateAsync(mode === 'reception'
        ? { numeroFournisseur: numero, issueDate: dateF, dueDate: echeance || undefined, receptionId, totalCents: enCentimes(ttc) }
        : { numeroFournisseur: numero, issueDate: dateF, dueDate: echeance || undefined, contactId, compteCharge: comptes.find((c) => c.id === compte)?.code,
            subtotalCents: enCentimes(ht), taxCents: enCentimes(tva), totalCents: ttcDirect });
      onClose();
    } catch (err) { setErreur(extractError(err).message); }
  };

  return (
    <form onSubmit={(e) => void envoyer(e)} className="ds-table-panel" style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <b>Nouvelle facture fournisseur</b>
        <button type="button" className="ds-row-btn" onClick={onClose} aria-label="Fermer"><X size={14} /></button>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button type="button" className={`ds-btn ds-btn-sm ${mode === 'reception' ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setMode('reception')}>Depuis une réception</button>
        <button type="button" className={`ds-btn ds-btn-sm ${mode === 'directe' ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setMode('directe')}>Directe (loyer, électricité…)</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
        {mode === 'reception' ? (
          <label style={CHAMP}>Réception
            <select value={receptionId} onChange={(e) => choisirReception(e.target.value)} required>
              <option value="">— Choisir —</option>
              {receptions.map((r) => <option key={r.id} value={r.id}>{r.reference} · {r.fournisseur} · {F(r.ttcCents)} F</option>)}
            </select>
          </label>
        ) : (
          <>
            <label style={CHAMP}>Fournisseur
              <select value={contactId} onChange={(e) => setContactId(e.target.value)} required>
                <option value="">— Choisir —</option>
                {(fournisseursPage?.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.companyName ?? c.fullName}</option>)}
              </select>
            </label>
            <label style={CHAMP}>Compte de charge
              <select value={compte} onChange={(e) => setCompte(e.target.value)} required>
                <option value="">— Choisir —</option>
                {comptesCharge.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.label}</option>)}
              </select>
            </label>
          </>
        )}
        <label style={CHAMP}>N° de la facture du fournisseur<input value={numero} onChange={(e) => setNumero(e.target.value)} required /></label>
        <label style={CHAMP}>Date de la facture<input type="date" value={dateF} onChange={(e) => setDateF(e.target.value)} required /></label>
        <label style={CHAMP}>Échéance (vide = à réception)<input type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)} /></label>
        {mode === 'reception' ? (
          <label style={CHAMP}>TTC facturé (FCFA)<input inputMode="decimal" value={ttc} onChange={(e) => setTtc(e.target.value)} required /></label>
        ) : (
          <>
            <label style={CHAMP}>HT (FCFA)<input inputMode="decimal" value={ht} onChange={(e) => setHt(e.target.value)} required /></label>
            <label style={CHAMP}>TVA (FCFA)<input inputMode="decimal" value={tva} onChange={(e) => setTva(e.target.value)} placeholder="0" /></label>
            <div style={CHAMP}>TTC<div className="mono" style={{ paddingTop: 8 }}>{F(BigInt(ttcDirect))} F</div></div>
          </>
        )}
      </div>
      {erreur && <div className="ds-alert-strip ds-alert-d" style={{ marginTop: 10 }}><div className="ds-alert-txt">{erreur}</div></div>}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" className="ds-btn ds-btn-p ds-btn-sm" disabled={creer.isPending}>Enregistrer la facture</button>
      </div>
    </form>
  );
};

/** Paiement (DAF) : total par défaut, partiel possible. */
const Paiement = ({ facture, onClose }: { facture: FactureFournisseur; onClose: () => void }) => {
  const restant = BigInt(facture.totalCents) - BigInt(facture.paidAmountCents);
  const [montant, setMontant] = useState(String(Number(restant) / 100));
  const [mode, setMode] = useState('virement');
  const [datePaiement, setDatePaiement] = useState(aujourdhui());
  const [reference, setReference] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const payer = usePayerFactureFournisseur();
  const envoyer = async (e: FormEvent) => {
    e.preventDefault(); setErreur(null);
    try {
      await payer.mutateAsync({ id: facture.id, montantCents: enCentimes(montant), mode, datePaiement, reference: reference || undefined });
      onClose();
    } catch (err) { setErreur(extractError(err).message); }
  };
  return (
    <form onSubmit={(e) => void envoyer(e)} className="ds-table-panel" style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <b>Payer {facture.reference} — {facture.contact.companyName ?? facture.contact.fullName} (reste {F(restant)} F)</b>
        <button type="button" className="ds-row-btn" onClick={onClose} aria-label="Fermer"><X size={14} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
        <label style={CHAMP}>Montant (FCFA)<input inputMode="decimal" value={montant} onChange={(e) => setMontant(e.target.value)} required /></label>
        <label style={CHAMP}>Mode
          <select value={mode} onChange={(e) => setMode(e.target.value)}>{MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select>
        </label>
        <label style={CHAMP}>Date<input type="date" value={datePaiement} onChange={(e) => setDatePaiement(e.target.value)} required /></label>
        <label style={CHAMP}>Référence (chèque, virement)<input value={reference} onChange={(e) => setReference(e.target.value)} /></label>
      </div>
      {erreur && <div className="ds-alert-strip ds-alert-d" style={{ marginTop: 10 }}><div className="ds-alert-txt">{erreur}</div></div>}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" className="ds-btn ds-btn-p ds-btn-sm" disabled={payer.isPending}><Banknote size={14} /> Payer</button>
      </div>
    </form>
  );
};
